import React from 'react'; 
import { useAppDispatch, useAppSelector } from '../../core/hooks';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import BackIcon from '@mui/icons-material/ArrowBack';
import HelpIcon from '@mui/icons-material/Help';
import { selectLevel, selectLevelNumber } from '../../core/reducers/reducer-user';
import { selectIndicatorsLastUpdated, selectSymbol, selectTimeframe, selectTraderType, selectView, updateSymbol, updateTimeframe, updateTraderType, updateView, setValuesBack, selectSecurityView, updateSecurityView } from '../../core/reducers/reducer-main';

import logo from '../../assets/images/askslim_new_blue.png'; 
import { asBlue } from '../../assets/colors/palette';
import styles from './header.module.css';
import { TimeframeTypes, isOfTypeTimeframe, isOfTypeTrader, isOfTypeView, traderToTimeframeAbbreviations, privilegedLevels, privilegedTimeframes, privilegedTraderTypes, isOfTypeSecurityView } from '../../core/datatypes/main';
import ScannerPopover from '../ScannerPopover';

const Header = () => {
    const dispatch = useAppDispatch(); 
    const timeframe = useAppSelector(selectTimeframe);
    const traderType = useAppSelector(selectTraderType);
    const view = useAppSelector(selectView);
    const securityView = useAppSelector(selectSecurityView);
    const symbol = useAppSelector(selectSymbol);
    const userLevel = useAppSelector(selectLevel);
    const userLevelNumber = useAppSelector(selectLevelNumber);
    const lastUpdate = useAppSelector(selectIndicatorsLastUpdated);

    const handleTimeframeChange = (event: SelectChangeEvent) => {
        if (isOfTypeTimeframe(event.target.value)) {
            dispatch(updateTimeframe(event.target.value))
        }
    }

    const handleTraderTypeChange = (event: SelectChangeEvent) => {
        if (isOfTypeTrader(event.target.value)) {
            if (view !== "traders-list") {
                dispatch(updateView(event.target.value));
            }
            dispatch(updateTraderType(event.target.value));
        }
    }

    const handleViewChange = (event: SelectChangeEvent) => {
        const nextView = event.target.value;
        if (nextView === "traders-list") {
            dispatch(updateTraderType('short_term'));
        } else if (nextView === "timeframes-list") {
            dispatch(updateTimeframe("two_hour"));
        }
        
        if (isOfTypeView(nextView)) {
            dispatch(updateView(nextView));
        }
    }   

    const handleSecurityViewChange = (event: SelectChangeEvent) => {
        if (isOfTypeSecurityView(event.target.value)) {
            dispatch(updateSecurityView(event.target.value))
        }
    }

    const onBackClicked = () => {
        dispatch(setValuesBack());
        dispatch(updateSymbol(''));
    }

    const backBtn = (
        <Button startIcon={<BackIcon />} variant="contained"
            style={{background: asBlue}}
            onClick={onBackClicked}
        >Back</Button>
    )

    const menuItems = [
        <MenuItem value={'monthly'} key={'monthly'}>Monthly</MenuItem>,
        <MenuItem value={'weekly'} key={'weekly'}>Weekly</MenuItem>,
        <MenuItem value={'daily'} key={'daily'}>Daily</MenuItem>,
        <MenuItem value={'two_hour'} key={'two_hour'}>Two Hour</MenuItem>,
        <MenuItem value={'fifteen_minute'} key={'fifteen_minute'}>Fifteen Minute</MenuItem>
    ]
    
    // if (privilegedLevels.indexOf(userLevel) !== -1) {
    //     menuItems.push(<MenuItem value={'three_minute'} key={'three_minute'}>Three Minute</MenuItem>)
    // } 

    if (userLevelNumber >= 4) {
        menuItems.push(<MenuItem value={'three_minute'} key={'three_minute'}>Three Minute</MenuItem>)
    } 


    const timeframeSelector = (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth >
                <InputLabel id="timeframe-select-label">Timeframe</InputLabel>
                <Select
                    labelId="timeframe-select-label"
                    id="timeframe-select"
                    value={timeframe}
                    label="Timeframe"
                    onChange={handleTimeframeChange}
                    style={{height: 64}}
                    sx = {{
                        '& .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '8px'
                        }
                    }}
                >
                    {menuItems}
                </Select>
            </FormControl>
        </Box>
    )

    const traderTypes = [
        <MenuItem value={'intermediate'} key={'intermediate'}>
            <div className={styles.traderMenuItem}>
                <div>Intermediate</div>
                <div className={styles.traderMenuItemSubText}>{ traderToTimeframeAbbreviations['intermediate'].join(' | ') }</div>
            </div>
        </MenuItem>,
        <MenuItem value={'short_term'} key={'short_term'}>
            <div className={styles.traderMenuItem}>
                <div>Short Term</div>
                <div className={styles.traderMenuItemSubText}>{ traderToTimeframeAbbreviations['short_term'].join(' | ') }</div>
            </div>
        </MenuItem>,
        <MenuItem value={'near_term'} key={'near_term'}>
            <div className={styles.traderMenuItem}>
                <div>Near Term</div>
                <div className={styles.traderMenuItemSubText}>{ traderToTimeframeAbbreviations['near_term'].join(' | ') }</div>
            </div>
        </MenuItem>
    ]; 
    // if (privilegedLevels.indexOf(userLevel) !== -1) {
    //     traderTypes.push(
    //         <MenuItem value={'intraday'} key={'intraday'}>
    //             <div className={styles.traderMenuItem}>
    //                 <div>Intraday</div>
    //                 <div className={styles.traderMenuItemSubText}>{ traderToTimeframeAbbreviations['intraday'].join(' | ') }</div>
    //             </div>
    //         </MenuItem>
    //     )
    // }
    if (userLevelNumber >= 4) {
        traderTypes.push(
            <MenuItem value={'intraday'} key={'intraday'}>
                <div className={styles.traderMenuItem}>
                    <div>Intraday</div>
                    <div className={styles.traderMenuItemSubText}>{ traderToTimeframeAbbreviations['intraday'].join(' | ') }</div>
                </div>
            </MenuItem>
        )
    }

    const traderTypeSelector = (view === "traders-list") ? (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth >
                <InputLabel id="trader-select-label">Trader Type</InputLabel>
                <Select
                    labelId="trader-select-label"
                    id="trader-select"
                    value={traderType}
                    label="Trader Type"
                    onChange={handleTraderTypeChange}
                    style={{height: 64}}
                    sx = {{
                        '& .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '8px'
                        }
                    }}
                >
                    {traderTypes}
                </Select>
            </FormControl>
        </Box>
    ) : (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth >
                <InputLabel id="trader-select-label">Trader Type</InputLabel>
                <Select
                    labelId="trader-select-label"
                    id="trader-select"
                    value={traderType}
                    label="Trader Type"
                    onChange={handleTraderTypeChange}
                    style={{height: 64}}
                    sx = {{
                        '& .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '8px'
                        }
                    }}
                >
                    <MenuItem value={'all'}>{ symbol ? 'All timeframes' : 'All'}</MenuItem>
                    {traderTypes}
                </Select>
            </FormControl>
        </Box>
    )

    const viewSelector = (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth >
                <InputLabel id="view-select-label">View</InputLabel>
                <Select
                    labelId="view-select-label"
                    id="view-select"
                    value={view}
                    label="View"
                    onChange={handleViewChange}
                    style={{height: 64}}
                    sx = {{
                        '& .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '8px'
                        }
                    }}
                >
                    <MenuItem value={'traders-list'}>Trader</MenuItem>
                    <MenuItem value={'timeframes-list'}>Timeframes</MenuItem>
                </Select>
            </FormControl>
        </Box>
    )

    const securityViewSelector = (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth >
                <InputLabel id="security-view-select-label">Security Type</InputLabel>
                <Select
                    labelId="security-view-select-label"
                    id="security-view-select"
                    value={securityView}
                    label="Security Type"
                    onChange={handleSecurityViewChange}
                    style={{height: 64}}
                    sx = {{
                        '& .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '8px'
                        }
                    }}
                >
                    <MenuItem value={'all'}>All</MenuItem>
                    <MenuItem value={'equity'}>Stocks</MenuItem>
                    <MenuItem value={'etfs'}>ETFs</MenuItem>
                </Select>
            </FormControl>
        </Box>
    )

    const leftComp = (view === 'timeframes-list') ? (
            <div className={styles.controlsCont}>
                {viewSelector}
                {timeframeSelector}
                {securityViewSelector}
            </div>
        ) : (view === "traders-list") ? (
            <div className={styles.controlsCont}>
                {viewSelector}
                {traderTypeSelector}
                {securityViewSelector}
            </div>
        ) : (
            <div className={styles.controlsCont}>
                {backBtn}
                <div className={styles.symbolText}>{symbol}</div>
                {traderTypeSelector}
            </div>
        );

    const rightComp = (view === "traders-list") ? (
        <div className={styles.scannerCont}>
            <ScannerPopover />
        </div>  
    ) : (undefined);

    const helpIconClicked = () => {
        window.open("https://askslim.com/mcm-usability-resources/", '_blank');
    };

    const lastUpdateDate = new Date(lastUpdate); 
    const options : Intl.DateTimeFormatOptions = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };

    const formattedDate = lastUpdateDate.toLocaleString('en-US', options);

    return (
        <>
            <div className={styles.mainHeading}>
                <div className={styles.logoCont}>
                    <img src={logo} height="64"/>
                </div>
                <div className={styles.titleText}>
                    Market Conditions Monitor
                    <sup style={{fontSize: '8pt'}}>TM</sup>
                    <sub style={{fontSize: '11pt'}}><a href="https://askslim.com/category/mcm-releases/" target="_blank" className={styles.versionTag}>v2.1 Pro</a></sub>
                </div>
            </div>
            <div className={styles.lastRefreshTime}> 
                <b>Last Refreshed:</b> <i style={{marginLeft: 4}}>{formattedDate}</i> 
                <Tooltip title="Usability Guide" placement='top'>
                    <IconButton onClick={helpIconClicked} sx={{color: asBlue}}>
                        <HelpIcon/>
                    </IconButton>
                </Tooltip>
            </div>
            <div className={styles.header}>
                <div className={styles.left}>
                    {leftComp}
                </div>
                <div className={styles.smartGap}></div>
                <div className={styles.right}>
                    {rightComp}
                </div>
            </div>
        </>
    )
}

export default Header;